/**
 * TextNote: a freestanding text annotation on the floor plan — e.g. "Bar
 * here", "Reserved", "Do not block doors". Per direct coordinator
 * feedback, this needs to be its own object type rather than a variant
 * of an existing one, since it has no real-world footprint, no seats,
 * and should never trigger spacing/clearance validation the way actual
 * furniture does (see domain/validation/rules/boundary.ts's explicit
 * exemption for this type).
 *
 * Editing: a text note's displayed content IS its `label` — the exact
 * same field every other object already exposes through the Inspector
 * panel's label input. This deliberately reuses existing, already-
 * working editing infrastructure rather than building a new double-
 * click-to-edit-in-place canvas/DOM overlay, which would be meaningfully
 * riskier to get right without the ability to test real mouse/touch
 * interaction directly.
 */

import { Group, Rect, Text } from 'react-konva';
import { useState } from 'react';
import type Konva from 'konva';
import type { LayoutObject, ObjectCatalogEntry } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

interface TextNoteProps {
  object: LayoutObject;
  catalogEntry: ObjectCatalogEntry;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  /** See ObjectRenderer.tsx and RoundTable.tsx for why this exists —
   * counter-rotates the text only, against the room's view orientation.
   * Especially important here: a text note's entire purpose is being
   * read, so an unreadable note defeats the feature outright. */
  roomRotationDegrees: number;
}

export function TextNote({ object, catalogEntry, isSelected, onSelect, onDragEnd, roomRotationDegrees }: TextNoteProps) {
  const widthPx = catalogEntry.defaultWidthFt * FEET_TO_PX;
  const heightPx = catalogEntry.defaultHeightFt * FEET_TO_PX;
  const [isDragging, setIsDragging] = useState(false);

  function handleDragStart(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(true);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grabbing';
  }

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(false);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grab';
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : isDragging ? 0.7 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      {/* A faint dashed outline, visible only while selected — a text
          note has no real footprint, so it shouldn't read as a solid
          object at rest the way furniture does, but it still needs to
          be easy to find and grab once selected. */}
      {isSelected && (
        <Rect
          x={-widthPx / 2 - 4}
          y={-heightPx / 2 - 4}
          width={widthPx + 8}
          height={heightPx + 8}
          stroke="#C9A35C"
          strokeWidth={1.5}
          dash={[4, 4]}
          cornerRadius={4}
        />
      )}

      <Text
        text={object.label}
        fontSize={13}
        fontStyle="italic"
        fontFamily="Inter, sans-serif"
        fill="#4D453C"
        width={widthPx}
        height={heightPx}
        offsetX={widthPx / 2}
        offsetY={heightPx / 2}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      {object.locked && (
        <Text
          text={'\u{1F512}'}
          fontSize={11}
          x={0}
          y={-(heightPx / 2 + 10)}
          offsetX={5.5}
          offsetY={5.5}
          listening={false}
        />
      )}
    </Group>
  );
}
